import api, { handleApiResponse, handleApiError } from './api';

class ConfigService {
  // Response handling helpers
  handleResponse(response) {
    return handleApiResponse(response);
  }

  handleError(error) {
    return handleApiError(error);
  }

  // Get system configuration (maintenance mode, user registration, etc.)
  async getSystemConfig() {
    try {
      const response = await api.get('/config/system');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update system configuration
  async updateSystemConfig(data) {
    try {
      const response = await api.post('/config/system', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get application configuration
  async getAppConfig() {
    try {
      const response = await api.get('/config/application');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update application configuration
  async updateAppConfig(data) {
    try {
      const response = await api.post('/config/application', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get system health status
  async getSystemStatus() {
    try {
      const response = await api.get('/config/status');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // FIXED: Ticket log methods - using correct endpoints
  async getTicketLogs(ticketId) {
    try {
      const response = await api.get(`/ticket-logs/${ticketId}`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getSystemLogs(filters = {}) {
    try {
      const response = await api.get('/ticket-logs/system', { 
        params: this.cleanParams(filters) 
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getLogAnalytics(filters = {}) {
    try {
      const response = await api.get('/ticket-logs/analytics', { 
        params: this.cleanParams(filters) 
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Validation methods
  validateSystemConfig(data) {
    const errors = {};
    
    if (data.app_name && data.app_name.length < 2) {
      errors.app_name = 'Application name must be at least 2 characters';
    }
    
    if (data.timezone && !this.isValidTimezone(data.timezone)) {
      errors.timezone = 'Invalid timezone';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Utility methods
  isValidTimezone(timezone) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch {
      return false;
    }
  }

  // Get available timezones for dropdowns
  getCommonTimezones() {
    return [
      { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
      { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
      { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
      { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
      { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
      { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
      { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
      { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST)' }
    ];
  }

  // Parse system status for display
  parseSystemStatus(statusData) {
    if (!statusData) return [];
    
    const statusArray = [];
    
    Object.entries(statusData).forEach(([component, status]) => {
      if (typeof status === 'object' && status !== null) {
        let message = status.message || 'No details available';
        let healthy = status.status === 'healthy';
        
        if (component === 'system_load' && status.memory_usage) {
          message = `Memory: ${status.memory_usage.usage_percentage || 0}% used`;
          healthy = status.status === 'healthy';
        }
        
        statusArray.push({
          component: this.formatComponentName(component),
          status: status.status || 'unknown',
          message: message,
          lastChecked: status.last_checked || null,
          healthy: healthy,
          responseTime: status.response_time_ms || null,
          details: status
        });
      }
    });
    
    return statusArray;
  }

  // Helper method to format component names
  formatComponentName(component) {
    const nameMap = {
      'database': 'Database',
      'cache': 'Cache System',
      'escalation_system': 'Escalation System',
      'ticket_system': 'Ticket System',
      'user_system': 'User System',
      'configuration': 'Configuration',
      'system_load': 'System Resources'
    };
    
    return nameMap[component] || component.charAt(0).toUpperCase() + component.slice(1).replace('_', ' ');
  }

  // Format log action names for display
  formatLogAction(action) {
    const actionMap = {
      'created': 'Created',
      'created_on_behalf': 'Created on Behalf',
      'updated': 'Updated',
      'assigned': 'Assigned',
      'assigned_by_expertise': 'Auto-Assigned',
      'escalated': 'Escalated',
      'auto_escalated': 'Auto-Escalated',
      'forwarded': 'Forwarded',
      'auto_forwarded': 'Auto-Forwarded',
      'status_changed': 'Status Changed',
      'priority_changed': 'Priority Changed',
      'responded': 'Responded',
      'agent_started_working': 'Started Working',
      'comment_added': 'Comment Added',
      'closed': 'Closed',
      'resolved': 'Resolved',
      'reopened': 'Reopened'
    };
    
    return actionMap[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  // Format duration from minutes to human readable
  formatDuration(minutes) {
    const roundedMinutes = Math.round(minutes);
    
    if (roundedMinutes < 60) {
      return `${roundedMinutes} minute${roundedMinutes !== 1 ? 's' : ''}`;
    }
    
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;
    
    if (hours < 24) {
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m`
        : `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    return remainingHours > 0
      ? `${days}d ${remainingHours}h`
      : `${days} day${days !== 1 ? 's' : ''}`;
  }

  // Clean parameters for API calls
  cleanParams(params) {
    const cleaned = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        cleaned[key] = params[key];
      }
    });
    return cleaned;
  }

  // Get status color class
  getStatusColor(status) {
    const colors = {
      'healthy': 'text-green-600 bg-green-100',
      'warning': 'text-yellow-600 bg-yellow-100',
      'critical': 'text-red-600 bg-red-100',
      'error': 'text-red-600 bg-red-100',
      'unknown': 'text-gray-600 bg-gray-100'
    };
    
    return colors[status] || colors.unknown;
  }
}

export default new ConfigService();